import type { OrderSummary } from '@gg/shared';

/** What /success shows for an order: confirmed, still waiting for the webhook, or not paid. */
export type OrderOutcome = 'paid' | 'waiting' | 'failed';

/**
 * Stripe's own word on the payment, from the `redirect_status` it adds to the return_url:
 * `succeeded`, or `processing` for a method that settles later. The order itself is only
 * marked paid by the webhook (invariant 1), which can land after this page loads.
 */
export const paymentTaken = (redirectStatus: string | null): boolean =>
  redirectStatus === 'succeeded' || redirectStatus === 'processing';

/**
 * FE-01: once Stripe says the payment went through, a `failed` or `expired` order is not final.
 * It is a retry on the same PaymentIntent after a decline, or a payment completed just after
 * the hold expired; the webhook pays it (`markOrderPaid` takes any order not paid or refunded),
 * so the page keeps waiting instead of telling a paying buyer it failed. A refunded order is
 * final either way.
 */
export function orderOutcome(status: OrderSummary['status'], taken: boolean): OrderOutcome {
  if (status === 'paid') return 'paid';
  if (status === 'pending') return 'waiting';
  if (taken && (status === 'failed' || status === 'expired')) return 'waiting';
  return 'failed';
}
